import { useCallback, useEffect, useState } from "react";
import { SquaresFour, CheckCircle, Crosshair } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));

export const Parking = ({ where }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [want, setWant] = useState({ fits: "car", basis: "day", units: 1, covered: false, secure: false, bay_id: "", plate: "" });
  const [quote, setQuote] = useState(null);
  const [mine, setMine] = useState({ name: "", kind: "works bay", fits: "car", where: "", walk_minutes: 3, covered: false, secure: false, spaces: 1, basis: "day" });

  const load = useCallback(() => {
    const q = where ? `?lat=${where.lat}&lng=${where.lng}` : "";
    api.get(`/parking${q}`).then(({ data: d }) => setData(d)).catch(oops);
  }, [where]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.post("/parking/quote", {
      fits: want.fits, basis: want.basis, units: Number(want.units) || 1,
      covered: want.covered, secure: want.secure,
    }).then(({ data: d }) => setQuote(d)).catch(() => setQuote(null));
  }, [want.fits, want.basis, want.units, want.covered, want.secure]);

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); load(); }
    catch (e) { oops(e); } finally { setBusy(false); }
  };

  if (!data) return null;
  const sym = quote?.symbol || data.zone?.symbol || "$";

  return (
    <div className="space-y-4" data-testid="parking-box">
      <div className="border border-border bg-white p-4" data-testid="parking-book-box">
        <p className="overline text-slate-500">
          <SquaresFour size={13} className="mr-1 inline align-[-2px] text-[#0B5C2E]" />
          Somewhere to leave the vehicle
        </p>
        <p className="mt-1 text-sm text-slate-600" data-testid="parking-note">
          Works bays, driveways, yards and laybys — by the hour, the day or the month. The more of
          you come in together, the fewer bays anybody needs.
        </p>
        <div className="mt-2 grid gap-2 sm:grid-cols-3">
          <select data-testid="parking-fits" value={want.fits} aria-label="What are you parking"
            onChange={(e) => setWant({ ...want, fits: e.target.value })}
            className="tap-safe border border-border bg-white px-3 py-2 text-sm">
            {data.fits.map((f) => <option key={f.key} value={f.key}>{f.name}</option>)}
          </select>
          <select data-testid="parking-basis" value={want.basis} aria-label="By the"
            onChange={(e) => setWant({ ...want, basis: e.target.value })}
            className="tap-safe border border-border bg-white px-3 py-2 text-sm">
            {data.bases.map((b) => <option key={b} value={b}>By the {b}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            How many
            <Input data-testid="parking-units" type="number" min={1} value={want.units}
              onChange={(e) => setWant({ ...want, units: e.target.value })} className="w-20" />
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" data-testid="parking-covered" checked={want.covered}
              onChange={(e) => setWant({ ...want, covered: e.target.checked })} />
            Covered
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" data-testid="parking-secure" checked={want.secure}
              onChange={(e) => setWant({ ...want, secure: e.target.checked })} />
            Secure or gated
          </label>
          <Input data-testid="parking-plate" placeholder="Plate (optional)"
            value={want.plate} onChange={(e) => setWant({ ...want, plate: e.target.value })} />
          <select data-testid="parking-bay-pick" value={want.bay_id} aria-label="Which space"
            onChange={(e) => setWant({ ...want, bay_id: e.target.value })}
            className="tap-safe border border-border bg-white px-3 py-2 text-sm sm:col-span-3">
            <option value="">Any space on the board</option>
            {data.bays.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name} · {b.fits_name} · {b.where || b.kind}
                {b.miles_away !== undefined ? ` · ${b.miles_away} mi away` : ""}
              </option>
            ))}
          </select>
        </div>
        {quote && (
          <p className="mt-3 text-sm text-slate-700" data-testid="parking-quote">
            {quote.fit} · {quote.units} {quote.basis}(s) — rate {sym}{Number(quote.charges.rate).toFixed(2)},
            tax {sym}{Number(quote.charges.tax).toFixed(2)} ·{" "}
            <span className="font-semibold text-[#0F172A]">
              {sym}{Number(quote.total).toFixed(2)} {(quote.currency || "").toUpperCase()}
            </span>
          </p>
        )}
        <Button className="mt-3" size="sm" data-testid="parking-book" disabled={busy || !quote}
          onClick={() => act(() => api.post("/parking/bookings", {
            ...want, units: Number(want.units) || 1, pay: "online",
          }), "Space booked")}>
          <CheckCircle size={14} className="mr-1.5" /> Take the space
        </Button>
      </div>

      <div data-testid="parking-bays">
        <p className="overline text-slate-500">Spaces on the board</p>
        {data.bays.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">Nothing listed yet — put yours up below.</p>
        ) : (
          <div className="mt-2 grid gap-2 sm:grid-cols-3">
            {data.bays.map((b) => (
              <div key={b.id} className="border border-border bg-white p-3" data-testid={`parking-bay-${b.id}`}>
                <p className="text-sm font-semibold text-[#0F172A]">{b.name}</p>
                <p className="text-xs text-slate-500">
                  {b.fits_name} · {b.kind}{b.where ? ` · ${b.where}` : ""} ·
                  {" "}{b.walk_minutes} min walk{b.covered ? " · covered" : ""}{b.secure ? " · secure" : ""}
                  {b.miles_away !== undefined ? ` · ${b.miles_away} mi from you` : ""}
                </p>
                <p className="mt-1 text-sm font-semibold text-[#0B5C2E]">
                  {b.symbol}{Number(b.rate || 0).toFixed(2)} per {b.basis}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div data-testid="parking-bookings">
        <p className="overline text-slate-500">Your spaces</p>
        <ul className="mt-2 space-y-2">
          {data.bookings.map((b) => (
            <li key={b.id} className="border border-border bg-white p-3" data-testid={`parking-booking-${b.id}`}>
              <p className="text-sm font-semibold text-[#0F172A]">
                {b.reference} · {b.fit} · {b.symbol}{Number(b.total).toFixed(2)}
              </p>
              <p className="text-xs text-slate-600">
                {b.units} {b.basis}(s) from {b.from_when} · {b.status}
                {b.plate ? ` · ${b.plate}` : ""}
              </p>
              {b.status !== "released" && (
                <Button className="mt-2" size="sm" variant="outline" data-testid={`parking-release-${b.id}`}
                  onClick={() => act(() => api.post(`/parking/bookings/${b.id}/release`), "Released")}>
                  Give it up
                </Button>
              )}
            </li>
          ))}
          {data.bookings.length === 0 && <li className="text-sm text-slate-500">No spaces booked.</li>}
        </ul>
      </div>

      <div className="border border-border bg-white p-4" data-testid="parking-list-yours">
        <p className="overline text-slate-500">Got a bay or a driveway going spare?</p>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <Input data-testid="parking-mine-name" placeholder="Name it — 'Driveway on Maple Street'"
            value={mine.name} onChange={(e) => setMine({ ...mine, name: e.target.value })} />
          <select data-testid="parking-mine-kind" value={mine.kind} aria-label="What kind"
            onChange={(e) => setMine({ ...mine, kind: e.target.value })}
            className="tap-safe border border-border bg-white px-3 py-2 text-sm">
            {data.kinds.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <select data-testid="parking-mine-fits" value={mine.fits} aria-label="What fits"
            onChange={(e) => setMine({ ...mine, fits: e.target.value })}
            className="tap-safe border border-border bg-white px-3 py-2 text-sm">
            {data.fits.map((f) => <option key={f.key} value={f.key}>{f.name}</option>)}
          </select>
          <select data-testid="parking-mine-basis" value={mine.basis} aria-label="Priced by"
            onChange={(e) => setMine({ ...mine, basis: e.target.value })}
            className="tap-safe border border-border bg-white px-3 py-2 text-sm">
            {data.bases.map((b) => <option key={b} value={b}>By the {b}</option>)}
          </select>
          <Input data-testid="parking-mine-where" placeholder="Where is it?"
            value={mine.where} onChange={(e) => setMine({ ...mine, where: e.target.value })} />
          <label className="flex items-center gap-2 text-sm text-slate-600">
            Spaces
            <Input data-testid="parking-mine-spaces" type="number" min={1} value={mine.spaces}
              onChange={(e) => setMine({ ...mine, spaces: e.target.value })} className="w-20" />
          </label>
        </div>
        <Button className="mt-2" size="sm" data-testid="parking-mine-save"
          disabled={busy || mine.name.trim().length < 2}
          onClick={() => act(async () => {
            const { data: d } = await api.post("/parking/bays", {
              ...mine, spaces: Number(mine.spaces) || 1,
              walk_minutes: Number(mine.walk_minutes) || 0,
              lat: where?.lat, lng: where?.lng,
            });
            setMine({ ...mine, name: "", where: "" });
            return d;
          }, "On the board")}>
          {where ? <><Crosshair size={14} className="mr-1.5" /> List it here</> : "List my space"}
        </Button>
      </div>
    </div>
  );
};
